/**
 * Pomodoro Tab — full-screen immersive timer.
 * Background fills viewport, timer floats centered, hamburger for all settings.
 */

import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { usePomodoroStore } from '@turnifly/shared/stores'
import { fetchPomodoroTasks } from '@turnifly/shared/api'
import { PomodoroTimer } from './pomodoro-timer'
import { PomodoroControls } from './pomodoro-controls'
import { PomodoroBackground } from './pomodoro-background'
import { PomodoroMenu } from './pomodoro-menu'
import { PomodoroMiniPlayer } from './pomodoro-mini-player'

export const PomodoroTab: React.FC = () => {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id) ?? ''

  // Prefetch tasks so menu opens instantly
  useEffect(() => {
    if (!userId) return
    qc.prefetchQuery({
      queryKey: ['pomodoro', 'tasks', userId],
      queryFn: () => fetchPomodoroTasks(userId),
      staleTime: 120_000,
    })
  }, [userId, qc])

  const mode = usePomodoroStore((s) => s.mode)
  const currentSession = usePomodoroStore((s) => s.currentSession)
  const sessionsBeforeLongBreak = usePomodoroStore((s) => s.settings.sessionsBeforeLongBreak)
  const backgroundType = usePomodoroStore((s) => s.settings.backgroundType)
  const backgroundValue = usePomodoroStore((s) => s.settings.backgroundValue)
  const activeHabitName = usePomodoroStore((s) => s.activeHabitName)
  const activeTaskText = usePomodoroStore((s) => s.activeTaskText)
  const isImmersive = usePomodoroStore((s) => s.status === 'running' && s.mode === 'work')
  const [isMenuOpen, setMenuOpen] = React.useState(false)

  const activeLabel = activeHabitName ?? activeTaskText

  return (
    <div className="absolute inset-0 z-[25]">
      {/* Full-screen background */}
      <PomodoroBackground
        type={backgroundType}
        value={backgroundValue}
        mode={mode}
      />

      {/* Content overlay */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center">
        {/* Top bar — session + hamburger (below header tabs ~130px) */}
        <div className={`absolute ${isImmersive ? 'top-[60px]' : 'top-[130px]'} left-4 right-4 flex items-center justify-between z-[35] transition-all duration-500`}>
          {/* Session badge */}
          <div className="px-3.5 py-1.5 rounded-full bg-black/20 backdrop-blur-xl border border-white/[0.15] shadow-[0_4px_16px_rgba(0,0,0,0.15)] flex items-center">
            <span className="text-[11px] font-bold text-white/90 uppercase tracking-[0.15em]">
              {t('pomodoro.session')} {currentSession}/{sessionsBeforeLongBreak}
            </span>
          </div>

          {/* Hamburger */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setMenuOpen(true)}
            className="w-10 h-10 rounded-full bg-black/20 backdrop-blur-xl border border-white/[0.15] flex items-center justify-center tap-highlight-transparent shadow-[0_4px_16px_rgba(0,0,0,0.15)]"
          >
            <Menu size={18} className="text-white/90" />
          </motion.button>
        </div>

        {/* Timer — centered */}
        <PomodoroTimer />

        {/* Controls — mode dots + start/pause */}
        <PomodoroControls />

        {/* Active context label */}
        <AnimatePresence>
          {activeLabel && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="mt-6 px-5 py-2.5 rounded-full bg-black/25 backdrop-blur-xl border border-white/[0.15] shadow-lg"
            >
              <p className="text-white/90 text-[13px] font-medium truncate max-w-[220px]">
                {activeLabel}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Menu drawer */}
      <AnimatePresence>
        {isMenuOpen && <PomodoroMenu onClose={() => setMenuOpen(false)} />}
      </AnimatePresence>

      {/* Mini floating timer */}
      <PomodoroMiniPlayer />
    </div>
  )
}
